import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Phone, Mail, MapPin, User, Calendar, Trash2, Edit2,
  Loader2, Save, MessageCircle, Receipt, Plus, Printer, FileText,
} from "lucide-react";
import DentalChart from "../components/DentalChart";
import AppointmentModal from "../components/AppointmentModal";
import BillForm from "../components/BillForm";
import AdvisedTreatments from "../components/AdvisedTreatments";
import PatientDocuments from "../components/PatientDocuments";
import { printExaminationRecord, printTreatmentPlan, printLetterhead } from "../lib/printing";

const TABS = ["Overview", "Examination", "Advised Treatment", "Documents", "Appointments", "Bills"];

const PatientDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { doctor } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("Overview");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [appts, setAppts] = useState([]);
  const [bills, setBills] = useState([]);
  const [showAppt, setShowAppt] = useState(false);
  const [showBill, setShowBill] = useState(null); // null | {} | bill

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, b] = await Promise.all([
        api.get(`/patients/${id}`),
        api.get(`/appointments`, { params: { patient_id: id } }),
        api.get(`/bills`, { params: { patient_id: id } }),
      ]);
      setPatient(p.data);
      setForm(p.data);
      setAppts(a.data);
      setBills(b.data);
    } catch (e) {
      if (e?.response?.status === 404) nav("/app/patients", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const savePatient = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.age === "" || payload.age === null) delete payload.age;
      else payload.age = parseInt(payload.age, 10);
      const res = await api.put(`/patients/${id}`, payload);
      setPatient(res.data);
      setForm(res.data);
      setEditing(false);
    } catch (e) {
      alert(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deletePatient = async () => {
    if (!window.confirm(`Delete ${patient.name}? All appointments, dental records and bills will also be removed.`)) return;
    try {
      await api.delete(`/patients/${id}`);
      nav("/app/patients", { replace: true });
    } catch {
      alert("Delete failed");
    }
  };

  const delAppt = async (aid) => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${aid}`);
    setAppts((x) => x.filter((a) => a.id !== aid));
  };

  const delBill = async (bid) => {
    if (!window.confirm("Delete this bill?")) return;
    await api.delete(`/bills/${bid}`);
    setBills((x) => x.filter((b) => b.id !== bid));
  };

  const waSendAppt = (a) => {
    const msg = `Hi ${patient.name},\n\nReminder: Your appointment with ${doctor?.name} is on ${a.date} at ${a.time}${a.reason ? `\nReason: ${a.reason}` : ""}\n\nThank you.`;
    const digits = (patient.phone || "").replace(/\D/g, "");
    const withCC = digits.length === 10 ? `91${digits}` : digits;
    window.open(`https://wa.me/${withCC}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) return <div className="p-8 text-slate-400">Loading...</div>;
  if (!patient) return null;

  const fld = (label, key, type = "text") => (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {editing ? (
        <input
          type={type}
          value={form[key] || ""}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2A5F]"
        />
      ) : (
        <div className="text-sm text-slate-800 py-2">{patient[key] || "-"}</div>
      )}
    </div>
  );

  return (
    <div className="p-8 max-w-6xl">
      <button onClick={() => nav("/app/patients")} className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back to patients
      </button>

      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-[#E6F7F5] text-[#0F2A5F] flex items-center justify-center text-xl font-bold">
              {patient.name?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{patient.name}</h1>
              <div className="text-sm text-slate-500 mt-0.5">
                UHID: <span className="font-mono text-slate-700">{patient.uhid}</span> • {patient.phone}
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => {
                // last visit = latest appointment date that is <= today
                const today = new Date().toISOString().slice(0, 10);
                const past = appts.filter((a) => a.date <= today).sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
                const lastVisit = past[0]?.date || "";
                printLetterhead({ doctor, patient, lastVisit });
              }}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm px-3 py-2 rounded-md"
            >
              <FileText size={14} /> Print Letterhead
            </button>
            <button onClick={() => setShowAppt(true)} className="inline-flex items-center gap-1.5 bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white text-sm px-3 py-2 rounded-md">
              <Calendar size={14} /> Book Appointment
            </button>
            <button onClick={() => setShowBill({})} className="inline-flex items-center gap-1.5 bg-[#14B8A6] hover:bg-[#0F8A7D] text-white text-sm px-3 py-2 rounded-md">
              <Receipt size={14} /> New Bill
            </button>
            <button onClick={deletePatient} className="inline-flex items-center gap-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 text-sm px-3 py-2 rounded-md">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t ? "border-[#0F2A5F] text-[#0F2A5F]" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Overview" && (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold text-slate-800">Personal Details</h3>
            {editing ? (
              <div className="flex gap-2">
                <button onClick={() => { setEditing(false); setForm(patient); }} className="text-sm px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
                <button onClick={savePatient} disabled={saving} className="text-sm px-3 py-1.5 bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white rounded inline-flex items-center gap-1.5">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                </button>
              </div>
            ) : (
              <button onClick={() => setEditing(true)} className="text-sm text-[#0F2A5F] hover:underline inline-flex items-center gap-1">
                <Edit2 size={13} /> Edit
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fld("Full Name", "name")}
            {fld("Phone", "phone", "tel")}
            {fld("Email", "email", "email")}
            {fld("Age", "age", "number")}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Gender</label>
              {editing ? (
                <select value={form.gender || ""} onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
                  <option value="">Select</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              ) : (<div className="text-sm text-slate-800 py-2">{patient.gender || "-"}</div>)}
            </div>
            {fld("Address", "address")}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
              {editing ? (
                <textarea rows={3} value={form.notes || ""} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
              ) : (<div className="text-sm text-slate-800 py-2 whitespace-pre-line">{patient.notes || "-"}</div>)}
            </div>
          </div>
        </div>
      )}

      {tab === "Examination" && (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={async () => {
                const rec = await api.get(`/patients/${id}/dental-record`);
                printExaminationRecord({ doctor, patient, teeth: rec.data.teeth || [] });
              }}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm px-3 py-1.5 rounded-md"
            >
              <Printer size={14} /> Print Examination
            </button>
          </div>
          <DentalChart patientId={id} />
        </>
      )}

      {tab === "Advised Treatment" && (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={async () => {
                const list = await api.get(`/patients/${id}/treatments`);
                printTreatmentPlan({ doctor, patient, items: list.data || [] });
              }}
              className="inline-flex items-center gap-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm px-3 py-1.5 rounded-md"
            >
              <Printer size={14} /> Print Treatment Plan
            </button>
          </div>
          <AdvisedTreatments
            patientId={id}
            onBillSelected={({ items }) => setShowBill({ prefillItems: items })}
          />
        </>
      )}

      {tab === "Documents" && <PatientDocuments patientId={id} />}

      {tab === "Appointments" && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">Appointments ({appts.length})</h3>
            <button onClick={() => setShowAppt(true)} className="text-sm bg-[#0F2A5F] hover:bg-[#0A1E4A] text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5">
              <Plus size={13} /> New
            </button>
          </div>
          {appts.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No appointments yet</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {appts.map((a) => (
                <li key={a.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{a.date} • {a.time}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{a.reason || "General"} • {a.status}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => waSendAppt(a)} title="Send WhatsApp reminder" className="p-1.5 text-[#25D366] hover:bg-green-50 rounded">
                      <MessageCircle size={16} />
                    </button>
                    <button onClick={() => delAppt(a.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {tab === "Bills" && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <h3 className="font-semibold text-slate-800">Bills ({bills.length})</h3>
            <button onClick={() => setShowBill({})} className="text-sm bg-[#14B8A6] hover:bg-[#0F8A7D] text-white px-3 py-1.5 rounded inline-flex items-center gap-1.5">
              <Plus size={13} /> New Bill
            </button>
          </div>
          {bills.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No bills yet</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3">Bill No</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Total</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {bills.map((b) => (
                  <tr key={b.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm font-mono text-slate-700">{b.bill_no}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{b.date}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{b.items?.length || 0}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-slate-800">₹{b.total?.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === "Paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.status}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <button onClick={() => setShowBill(b)} className="text-[#0F2A5F] hover:underline mr-3">View</button>
                      <button onClick={() => delBill(b.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <AppointmentModal
        open={showAppt}
        patient={patient}
        doctor={doctor}
        onClose={() => setShowAppt(false)}
        onCreated={(a) => setAppts((prev) => [...prev, a])}
      />

      {showBill !== null && (
        <BillForm
          patient={patient}
          doctor={doctor}
          initial={showBill?.id ? showBill : undefined}
          initialItems={showBill?.prefillItems}
          onClose={() => setShowBill(null)}
          onSaved={(b, isNew) => {
            if (isNew) {
              setBills((prev) => [b, ...prev]);
              setTab("Bills");
            } else {
              setBills((prev) => prev.map((x) => (x.id === b.id ? b : x)));
            }
          }}
        />
      )}
    </div>
  );
};

export default PatientDetail;
